<template>
    <span v-bind="forwarded" :data-ms-fallback="fallbackSrc">
        <img
            :src="src"
            alt=""
            role="presentation"
            decoding="async"
            loading="lazy"
            class="w-[1em] h-[1em] max-h-[4rem] object-contain shrink-0 pointer-events-none select-none"
            :class="{ 'brightness-0 invert': needsLightOnDark }"
            data-ms-icon="1"
            @error="onImgError"
        />
    </span>
</template>

<script setup>
import { computed, useAttrs, normalizeClass } from 'vue';

defineOptions({ inheritAttrs: false });

const FALLBACK = '/img/svg/ms/help.svg';

const props = defineProps({
    name: { type: String, required: true },
    /** Use when parent has light text but this span has no `text-white` class (e.g. CTA buttons). */
    onDark: { type: Boolean, default: false }
});

const attrs = useAttrs();

function classStringFromAttrs() {
    const c = attrs.class;
    if (c == null) return '';
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) return c.filter(Boolean).join(' ');
    if (typeof c === 'object') return Object.keys(c).filter((k) => c[k]).join(' ');
    return String(c);
}

const needsLightOnDark = computed(
    () => props.onDark || /\btext-white\b/.test(classStringFromAttrs())
);

const glyph = computed(() => String(props.name || '').replace(/[^a-z0-9_]/gi, ''));
const src = computed(() => `/img/svg/ms/${glyph.value}.svg`);
const fallbackSrc = FALLBACK;

const forwarded = computed(() => {
    const { class: c, ...rest } = attrs;
    const base =
        'inline-flex items-center justify-center leading-none [&>img]:max-w-none';
    return {
        ...rest,
        class: normalizeClass([base, c])
    };
});

function onImgError(e) {
    const el = e.target;
    if (el.dataset.fb) return;
    el.dataset.fb = '1';
    const url = el.parentElement?.getAttribute('data-ms-fallback');
    if (url) el.src = url;
}
</script>
